/* DealAutocomplete — vyhledávání obchodního případu pro vybraného klienta.
 *
 * Pokud je contactId vyplněn, načteme deals filtered by contact_id;
 * jinak fetch ALL open deals a hledáme.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Briefcase, Search, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { TL } from "@/lib/prekladyUI";

const fmtBJ = (n) => new Intl.NumberFormat("cs-CZ").format(Math.round(n || 0));

export default function DealAutocomplete({
  contactId,
  value,
  onChange,
  testid = "deal-autocomplete",
  placeholder,
  disabled = false,
}) {
  const { t, lang } = useLanguage();
  const sk = lang === "sk";
  const phEff = placeholder || (t("auto.dealAutocomplete.najdiObchodniPripadKlienta"));
  const dealFallback = t("auto.dealAutocomplete.obchodniPripad");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);

  // Fetch deals — refetch when contactId changes
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (contactId) params.set("contact_id", contactId);
    params.set("result", "open");
    api.get(`/api/deals?${params.toString()}`)
      .then((r) => {
        if (!mounted) return;
        setItems(Array.isArray(r.data) ? r.data : []);
        setLoading(false);
      })
      .catch(() => { if (mounted) { setItems([]); setLoading(false); } });
    return () => { mounted = false; };
  }, [contactId]);

  const selected = useMemo(() => items.find((d) => d.id === value) || null, [items, value]);
  const [display, setDisplay] = useState("");
  useEffect(() => {
    if (selected && !open) setDisplay(`${selected.subject || selected.title || dealFallback}`);
    else if (!selected && !open) setDisplay("");
  }, [selected, open, dealFallback]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items.slice(0, 10);
    return items.filter((d) => {
      const subject = (d.subject || d.title || "").toLowerCase();
      const product = (d.product_type || "").toLowerCase();
      const provider = (d.provider || "").toLowerCase();
      return subject.includes(term) || product.includes(term) || provider.includes(term);
    }).slice(0, 12);
  }, [items, q]);

  useEffect(() => { setHi(0); }, [q]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (d) => {
    onChange?.(d?.id || "", d);
    setDisplay(d ? `${d.subject || d.title || dealFallback}` : "");
    setQ("");
    setOpen(false);
  };

  const clear = () => { onChange?.("", null); setQ(""); setDisplay(""); };

  const onKey = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") { setOpen(true); e.preventDefault(); }
      return;
    }
    if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) e.stopPropagation();
    if (e.key === "ArrowDown") { setHi((h) => Math.min(filtered.length - 1, h + 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setHi((h) => Math.max(0, h - 1)); e.preventDefault(); }
    else if (e.key === "Enter") { if (filtered[hi]) { pick(filtered[hi]); e.preventDefault(); } }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
        <input
          value={open ? q : (selected ? display : q)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={contactId ? phEff : (t("auto.dealAutocomplete.nejprveVyberKlienta"))}
          disabled={disabled || !contactId}
          data-testid={`${testid}-input`}
          className="w-full rounded-md px-2 py-1.5 pl-7 pr-7 text-maly outline-none disabled:opacity-50"
          style={{ background: "var(--bg)", border: "1px solid var(--line)", color: "var(--ink)", height: 32, lineHeight: 1.4 }}
        />
        {loading && (
          <Loader2 size={12} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin" style={{ color: "var(--muted)" }} />
        )}
        {!loading && (value || q) && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded flex items-center justify-center hover:bg-black/5"
            aria-label={t("auto.dealAutocomplete.vymazat")}
          >
            <X size={11} style={{ color: "var(--muted)" }} />
          </button>
        )}
      </div>
      {open && contactId && (
        <div
          className="absolute left-0 right-0 mt-1 rounded-lg overflow-hidden z-50"
          style={{
            background: "var(--surface)", border: "1px solid var(--line)",
            boxShadow: "0 12px 32px rgba(40, 25, 15, 0.15), 0 2px 6px rgba(40, 25, 15, 0.06)",
            maxHeight: 280, overflowY: "auto",
          }}
          data-testid={`${testid}-dropdown`}
        >
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-drobny" style={{ color: "var(--muted)" }}>
              {t("auto.dealAutocomplete.zadnyOtevrenyObchodniPripad")} {q.trim() ? `(${TL("filtr", lang)}: „${q}“)` : ""}
            </div>
          )}
          {filtered.map((d, i) => (
            <button
              key={d.id}
              type="button"
              onClick={() => pick(d)}
              onMouseEnter={() => setHi(i)}
              data-testid={`${testid}-item-${d.id}`}
              className="w-full text-left px-2.5 py-1.5 text-[12px] flex items-center gap-2 transition-colors"
              style={{ background: hi === i ? "var(--accent-soft)" : "transparent", color: "var(--ink)" }}
            >
              <Briefcase size={11} style={{ color: "var(--accent)", flexShrink: 0 }} />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{d.subject || d.title || dealFallback}</div>
                <div className="text-drobny flex items-center gap-1.5 truncate" style={{ color: "var(--muted)" }}>
                  {d.product_type && <span className="uppercase font-semibold">{d.product_type}</span>}
                  {d.stage_label && <span>· {d.stage_label}</span>}
                  {d.value_bj > 0 && <span>· {fmtBJ(d.value_bj)} BJ</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
